import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Sparkles, BarChart3, ShoppingBag, MessageCircle, ArrowRight, Heart } from "lucide-react";

const features = [
  { icon: Sparkles, title: "AI Copy Studio", desc: "Generate scroll-stopping captions & campaign copy in seconds.", ic90: "bg-fuchsia-100", ic: "text-fuchsia-600" },
  { icon: BarChart3, title: "Campaign Analytics", desc: "Track impressions, clicks and conversions in one bright dashboard.", ic90: "bg-blue-100", ic: "text-blue-600" },
  { icon: ShoppingBag, title: "Deal Marketplace", desc: "Browse and book sponsored content packages with top creators.", ic90: "bg-rose-100", ic: "text-rose-600" },
  { icon: MessageCircle, title: "Direct Collabs", desc: "Message brands and creators to close deals without the back-and-forth.", ic90: "bg-pink-100", ic: "text-pink-600" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDF8FA] overflow-x-hidden">
      <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-pink-500/30">
            <Zap className="h-5 w-5 text-white" fill="white" />
          </div>
          <span className="font-display font-extrabold text-xl">MrktMe</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login" className="px-5 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-white transition-all">Log in</Link>
          <Link to="/register" data-testid="landing-signup" className="px-5 py-2.5 rounded-full font-bold text-white brand-gradient shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-200">Get started</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-fuchsia-100 text-fuchsia-700 font-semibold text-sm mb-6">
            <Sparkles className="h-4 w-4" /> Where creators & brands click
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-slate-900">
            The social network <br /> built for <span className="gradient-text">marketing</span> collabs.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-md leading-relaxed">
            Post content, launch campaigns, hire creators and generate winning copy with AI — all in one bright, welcoming home.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/register" data-testid="hero-cta" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-white brand-gradient shadow-xl shadow-pink-500/30 hover:-translate-y-0.5 transition-all duration-200">
              Join free <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/login" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-slate-700 bg-white border border-slate-200 hover:border-pink-300 transition-all duration-200">
              I have an account
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-3 text-sm text-slate-500">
            <div className="flex -space-x-2">
              {["6988571","6988678","9780959"].map((id) => (
                <img key={id} src={`https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&w=80`} className="h-9 w-9 rounded-full ring-2 ring-white object-cover" alt="creator" />
              ))}
            </div>
            <span className="font-medium">Loved by 10,000+ creators & brands</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
          <div className="absolute -inset-6 brand-gradient rounded-[2.5rem] blur-2xl opacity-20" />
          <img src="https://images.pexels.com/photos/6988571/pexels-photo-6988571.jpeg?auto=compress&w=800"
            alt="creator" className="relative rounded-3xl shadow-2xl shadow-pink-500/20 w-full object-cover aspect-[4/5]" />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-rose-100 flex items-center justify-center"><Heart className="h-5 w-5 text-rose-500" fill="currentColor" /></div>
            <div><p className="font-bold text-slate-900 text-sm">+248% reach</p><p className="text-xs text-slate-500">last campaign</p></div>
          </div>
          <div className="absolute -top-5 -right-2 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-fuchsia-100 flex items-center justify-center"><Sparkles className="h-5 w-5 text-fuchsia-600" /></div>
            <div><p className="font-bold text-slate-900 text-sm">AI copy</p><p className="text-xs text-slate-500">in seconds</p></div>
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(236,72,153,0.12)] transition-all duration-200">
                <div className={`h-12 w-12 rounded-xl ${f.ic90} flex items-center justify-center mb-4`}>
                  <Icon className={`h-6 w-6 ${f.ic}`} strokeWidth={2.4} />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{f.title}</h3>
                <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
